/**
 * Subscribes to the /api/positions/stream WebSocket and exposes two indexed
 * maps that the Map page uses to render markers:
 *
 *   - `mountpoints` keyed by mountpoint name (latest base position)
 *   - `rovers` keyed by NTRIP session_id (latest rover GGA state)
 *
 * The hook handles the initial snapshot, incremental updates, the
 * `rover_removed` events that fire when a rover disconnects and the
 * `mountpoint_removed` events that fire when a mountpoint is deleted.
 * Connection reconnects with exponential backoff (max 10s) on abnormal close.
 */

import { useEffect, useRef, useState } from "react";

import { TOKEN_STORAGE_KEY, wsBase } from "@/lib/api";
import type {
  MountpointPosition,
  PositionUpdate,
  RoverPosition,
} from "@/types/api";

export type StreamStatus = "idle" | "connecting" | "open" | "closed" | "error";

export interface PositionsState {
  mountpoints: Map<string, MountpointPosition>;
  rovers: Map<string, RoverPosition>;
  status: StreamStatus;
}

interface UsePositionsStreamOptions {
  enabled: boolean;
}

const MAX_BACKOFF_MS = 10_000;

export function usePositionsStream(
  options: UsePositionsStreamOptions = { enabled: true },
): PositionsState {
  const { enabled } = options;
  const [state, setState] = useState<PositionsState>(() => ({
    mountpoints: new Map(),
    rovers: new Map(),
    status: "idle",
  }));

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const url = `${wsBase()}/api/positions/stream?token=${encodeURIComponent(token)}`;
      setState((prev) => ({ ...prev, status: "connecting" }));
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        reconnectAttemptsRef.current = 0;
        setState((prev) => ({ ...prev, status: "open" }));
      };

      ws.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        let msg: PositionUpdate;
        try {
          msg = JSON.parse(event.data) as PositionUpdate;
        } catch {
          return;
        }
        setState((prev) => applyMessage(prev, msg));
      };

      ws.onerror = () => {
        setState((prev) => ({ ...prev, status: "error" }));
      };

      ws.onclose = (event) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, status: "closed" }));
        if (event.code === 1000) return; // normal — drop reconnect
        const attempt = reconnectAttemptsRef.current;
        reconnectAttemptsRef.current = attempt + 1;
        const delay = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS);
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        try {
          ws.close(1000, "unmount");
        } catch {
          /* noop */
        }
      }
    };
  }, [enabled]);

  return state;
}

function applyMessage(prev: PositionsState, msg: PositionUpdate): PositionsState {
  if (msg.type === "snapshot") {
    const mountpoints = new Map<string, MountpointPosition>();
    msg.mountpoints.forEach((mp) => mountpoints.set(mp.name, mp));
    const rovers = new Map<string, RoverPosition>();
    msg.rovers.forEach((r) => rovers.set(r.session_id, r));
    return { ...prev, mountpoints, rovers };
  }
  if (msg.type === "update" && msg.kind === "mountpoint") {
    const mountpoints = new Map(prev.mountpoints);
    mountpoints.set(msg.data.name, msg.data);
    return { ...prev, mountpoints };
  }
  if (msg.type === "update" && msg.kind === "rover") {
    const rovers = new Map(prev.rovers);
    rovers.set(msg.data.session_id, msg.data);
    return { ...prev, rovers };
  }
  if (msg.type === "update" && msg.kind === "rover_removed") {
    if (!prev.rovers.has(msg.session_id)) return prev;
    const rovers = new Map(prev.rovers);
    rovers.delete(msg.session_id);
    return { ...prev, rovers };
  }
  if (msg.type === "update" && msg.kind === "mountpoint_removed") {
    if (!prev.mountpoints.has(msg.name)) return prev;
    const mountpoints = new Map(prev.mountpoints);
    mountpoints.delete(msg.name);
    return { ...prev, mountpoints };
  }
  return prev;
}

// Exported for unit-style e2e assertions; the hook itself is the public API.
export { applyMessage as _applyMessageForTests };
